import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {TranslateService} from '@ngx-translate/core';
import {concatMap, filter, map, switchMap, tap} from 'rxjs/operators';
import {SelectItem} from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import {firstValueFrom, from, Observable, Subscription} from 'rxjs';
import {DynamicFormReadyEvent, FilterSelectFieldConfig} from '@mydocma/dynamic-form';
import {FaultField} from '../fault-field';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmAttributeDropdownTileComponent} from '../mm-attribute-dropdown-tile/mm-attribute-dropdown-tile.component';
import {MmPlanViewService} from '../../../mm-plans-view/mm-plan-view/services/mm-plan-view.service';
import {ViewFault} from '@docma-mm-core/fault/fault';

@Injectable()
@UntilDestroy()
export class MmAttributeService {

  private hasAttributeGroupField = false;
  private hasAttributeField = false;

  attributeGroups$ = this.projectLoaderService.attributes.pipe(filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
        map(a => a.data.filter(f => !f.IsDeleted && !f.IsInactive).map(x => x.AttributeGroup.Id).reduce((arr: number[], item: number) =>  arr.find(p => p === item) ? arr : [...arr, item], []) ))
    .pipe(switchMap(usedAttributeGroupIds =>
      this.projectLoaderService.attributeGroups.pipe(filter(ag => ag.loadState.status == LoadStatusEnum.Loaded))
        .pipe(switchMap(attributeGroups => this.projectLoaderService.attributeGroupsFiles.pipe(filter(agf => agf.loadState.status == LoadStatusEnum.Loaded))
          .pipe(map(attributeGroupsFiles => attributeGroups.data.filter(f => usedAttributeGroupIds.findIndex(u => u === f.Id) !== -1).map(x => ({id: x.Id, text: x.Name1, data: attributeGroupsFiles.data.find(gf => gf.Id === x.Id) }))
            .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], [])
          ))
      ))
    ), untilDestroyed(this));

  attributes$: Observable<Array<SelectItem>>;

  formReadyEvent: DynamicFormReadyEvent;
  configName: string;

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly translateService: TranslateService,
              private readonly planViewService: MmPlanViewService) {
  }

  get attributeGroup() {
    if (this.hasAttributeGroupField) {
      return this.formReadyEvent.dynamicFormService.getFieldValue(this.configName + 'Group_Image');
    }
    return null;
  }

  get attribute() {
    if (this.hasAttributeField) {
      return this.formReadyEvent.dynamicFormService.getFieldValue(this.configName);
    }
    return null;
  }

  fault: ViewFault;
  initAttributeId?: number;
  attributeSubscription: Subscription;
  onAttributeGroupChangedSubscription: Subscription;
  onAttributeChangedSubscription: Subscription;

  preInitialize() {
    this.attributeSubscription?.unsubscribe();
    this.onAttributeGroupChangedSubscription?.unsubscribe();
    this.onAttributeChangedSubscription?.unsubscribe();
  }



  async initialize(fault: ViewFault, configName: string, attributeField: FaultField, attributeGroupField: FaultField, formReadyEvent: DynamicFormReadyEvent) {
    this.formReadyEvent = formReadyEvent;
    this.configName = configName;

    this.fault = fault;
    this.initAttributeId = this.attribute;

    if (attributeGroupField) {
      this.hasAttributeGroupField = true;

      const attributeGroupFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName + 'Group_Image') as FilterSelectFieldConfig;
      attributeGroupFieldConfig.source = await firstValueFrom(this.attributeGroups$);
      attributeGroupFieldConfig.valueItemComponent = MmAttributeDropdownTileComponent;
      attributeGroupFieldConfig.sourceItemComponent = MmAttributeDropdownTileComponent;
      this.formReadyEvent.dynamicFormService.updateFieldConfig(attributeGroupFieldConfig);

      this.onAttributeChangedSubscription = this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
        .pipe(untilDestroyed(this),
          filter((param: any) => param.config.name === this.configName),
        ).subscribe(val => {
          if (val.value != null && this.attributeGroup == null) {
            this.setSelectedAttributeGroup();
          }
          else if (val.value == null) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName + 'Group_Image', null);
            this.loadAttributes();
          }

          // MMW-890
          this.planViewService.latestAttribute = val.value;
          // if (this.fault) {
          //   this.planViewService.updatePin(this.fault, val.value);
          // }
        });
    }

    if (attributeField) {
      this.hasAttributeField = true;
      const attributeFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName) as FilterSelectFieldConfig;

      if (this.attribute) {
        this.setSelectedAttributeGroup(true);
      }

      this.loadAttributes(true);

      if (this.attribute) {
        // console.warn(`set contact CO ${this.contact}`)
        this.formReadyEvent.dynamicFormService.updateFieldValue(attributeFieldConfig.name, this.attribute);
      }
      else {
        // console.warn(`set contact CO NULL`)
        this.formReadyEvent.dynamicFormService.updateFieldValue(attributeFieldConfig.name, null);
      }

      this.onAttributeGroupChangedSubscription = this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
        .pipe(untilDestroyed(this),
          filter((param: any) => param.config.name === this.configName + 'Group_Image'),
          tap(attributeGroup => this.loadAttributes()),
          tap(attributeGroup => {
            if (attributeGroup.value == null && this.initAttributeId !== null) {
              // this.faultFormGroup.markAsDirty();
            }
          }),
          filter(attributeGroup => !!attributeGroup.value),
          switchMap(attributeGroup => this.attributes$),
          filter(attributes => {
            const g = this.formReadyEvent.dynamicFormService.getFieldValue(attributeFieldConfig.name);
            const found = (attributes?.findIndex(f => g && f.id === g) ?? 0);
            return attributes.length > 0 && found === -1;
          }),
          map(attributes => attributes[0]),
          tap(attribute => {
            // console.warn(`set attribute by attributeGroup name: ${attribute.text}, id: ${attribute.id}`)
            this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName, attribute.id);
          }),
          filter(attribute => this.initAttributeId == null || this.initAttributeId !== attribute.id),
          untilDestroyed(this)
        ).subscribe(contact =>  {
            // this.faultFormGroup.markAsDirty()
          }
        );
    }
  }

  loadAttributes(firstLoad: boolean = false): void {
    if (this.attributeGroup != null) {
      this.loadAttributesByAttributeGroup(this.attributeGroup);
    }
    else {
      this.loadAttributesNotFiltered(firstLoad);
    }
  }
  setSelectedAttributeGroup(firstLoad: boolean = false): void {
    if (!this.hasAttributeGroupField){
      return;
    }

    this.projectLoaderService.attributes.pipe(
      filter(m => m?.loadState.status === LoadStatusEnum.Loaded),
      map(m =>  m.data.filter(c => c.Id === this.attribute)),
      concatMap(attributes => from(attributes)),
      filter(attribute => !!attribute),
      untilDestroyed(this)
    ).subscribe(attribute => {
      // console.warn(`set company name: ${contact.Company.ShortName}, id: ${contact.Company.Id}`)
      this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName + 'Group_Image', attribute.AttributeGroup.Id)

      if (!firstLoad) {
        this.loadAttributes();
      }
    });
  }

  loadAttributesNotFiltered(firstLoad: boolean) {
    if(!firstLoad) {
      if (this.formReadyEvent.dynamicFormService.getFieldValue(this.configName)) {
        this.formReadyEvent.dynamicFormService.updateFieldValue(this.configName, null)
      }
    }

    // this.attributes$ = this.projectLoaderService.attributes.pipe(filter(a => a.loadState.status == LoadStatusEnum.Loaded))
    //   .pipe(switchMap(attributes => this.projectLoaderService.attributeGroups.pipe(filter(ag => ag.loadState.status == LoadStatusEnum.Loaded))
    //     .pipe(switchMap(attributeGroups => this.projectLoaderService.attributeGroupsFiles.pipe(filter(agf => agf.loadState.status == LoadStatusEnum.Loaded))
    //       .pipe(map(attributeGroupFiles => //({attributes: attributes, attributeGroupFiles: attributeGroupFiles, attributeGroups: attributeGroups})
    //         attributes.data.filter(f => !f.IsDeleted && !f.IsInactive).map(x => ({id: x.Id, text: x.Name1, data: attributeGroupFiles.data.find(gf => gf.Id === x.AttributeGroup.Id)}))
    //           .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], [])
    //       ))
    //     ))
    //   ));

    this.attributes$ = this.projectLoaderService.attributes.pipe(
      filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
      map(attributes => attributes.data.filter(f => !f.IsDeleted && !f.IsInactive).map(x => ({
        id: x.Id,
        text: x.Name2 && x.Name2.trim() ? `${x.Name1} - ${x.Name2}` : x.Name1,
        data: null
      }))
        .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []))
    );

    this.setAttributesSource();
  }

  loadAttributesByAttributeGroup(attributeGroupId: number) {

    // this.attributes$ = this.projectLoaderService.attributes.pipe(filter(a => a.loadState.status == LoadStatusEnum.Loaded))
    //   .pipe(switchMap(attributes => this.projectLoaderService.attributeGroups.pipe(filter(ag => ag.loadState.status == LoadStatusEnum.Loaded))
    //     .pipe(switchMap(attributeGroups => this.projectLoaderService.attributeGroupsFiles.pipe(filter(agf => agf.loadState.status == LoadStatusEnum.Loaded))
    //       .pipe(map(attributeGroupFiles => //({attributes: attributes, attributeGroupFiles: attributeGroupFiles, attributeGroups: attributeGroups})
    //         attributes.data.filter(f => !f.IsDeleted && !f.IsInactive && f.AttributeGroup.Id === attributeGroupId).map(x => ({id: x.Id, text: x.Name1, data: attributeGroupFiles.data.find(gf => gf.Id === x.AttributeGroup.Id)}))
    //           .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], [])
    //       ))
    //     ))
    //   ));

    this.attributes$ = this.projectLoaderService.attributes.pipe(
      filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
      map(attributes => attributes.data.filter(f => !f.IsDeleted && !f.IsInactive && f.AttributeGroup.Id === attributeGroupId).map(x => ({
        id: x.Id,
        text: x.Name2 && x.Name2.trim() ? `${x.Name1} - ${x.Name2}` : x.Name1,
        data: null
      }))
        .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []))
    );

    this.setAttributesSource();
  }

  setAttributesSource() {
    firstValueFrom(this.attributes$)
      .then(attributes => {
        const attributeFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(this.configName) as FilterSelectFieldConfig;

        attributeFieldConfig.source = attributes;
        this.formReadyEvent.dynamicFormService.updateFieldConfig(attributeFieldConfig);
      });
  }




}
